import React, { useState, useEffect } from "react";
import "./ScoringConfigPage.css";
import { apiGet, apiPut } from "../../services/api";
import { ScoringConfig } from "../../types";
import { FormField, Button } from "../shared/FormField";

export function ScoringConfigPage() {
  const [config, setConfig] = useState<ScoringConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [redFlagInput, setRedFlagInput] = useState("");

  useEffect(() => { loadConfig(); }, []);

  const loadConfig = async () => {
    try {
      const result = await apiGet<ScoringConfig>("/api/scoring-config");
      setConfig(result);
    } catch (err) {
      console.error("Failed to load scoring config:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateKPI = (index: number, field: string, value: string | number) => {
    if (!config) return;
    const kpis = [...config.kpis];
    kpis[index] = { ...kpis[index], [field]: value };
    setConfig({ ...config, kpis });
  };

  const addRedFlag = () => {
    if (!config || !redFlagInput.trim()) return;
    setConfig({ ...config, redFlagPhrases: [...config.redFlagPhrases, redFlagInput.trim()] });
    setRedFlagInput("");
  };

  const removeRedFlag = (index: number) => {
    if (!config) return;
    setConfig({ ...config, redFlagPhrases: config.redFlagPhrases.filter((_, i) => i !== index) });
  };

  const handleSave = async () => {
    if (!config) return;
    setSaving(true);
    try {
      await apiPut("/api/scoring-config", {
        kpis: config.kpis,
        redFlagPhrases: config.redFlagPhrases,
        methodology: config.methodology,
      });
    } catch (err) {
      console.error("Failed to save scoring config:", err);
    } finally {
      setSaving(false);
    }
  };

  const totalWeight = config?.kpis.reduce((sum, k) => sum + k.weight, 0) || 0;

  if (loading) return <div className="protrain-admin-loading">Loading scoring config...</div>;
  if (!config) return <div className="protrain-admin-empty">Failed to load config.</div>;

  return (
    <div className="protrain-scoring-page">
      <div className="protrain-admin-header">
        <h2 className="protrain-page-title">Scoring Configuration</h2>
        <Button onClick={handleSave} disabled={saving || Math.abs(totalWeight - 1) > 0.01}>
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>

      <div className="protrain-scoring-section">
        <h3>KPI Weights</h3>
        <p className="protrain-scoring-hint">
          Weights must sum to 1.0. Current: <span className={Math.abs(totalWeight - 1) > 0.01 ? "protrain-scoring-weight-error" : "protrain-scoring-weight-ok"}>{totalWeight.toFixed(2)}</span>
        </p>
        <div className="protrain-scoring-kpis">
          {config.kpis.map((kpi, i) => (
            <div key={i} className="protrain-scoring-kpi-row">
              <div className="protrain-scoring-kpi-name">{kpi.name.replace(/_/g, " ")}</div>
              <div className="protrain-scoring-kpi-fields">
                <FormField label="Weight">
                  <input type="number" step="0.05" min="0" max="1" value={kpi.weight} onChange={(e) => updateKPI(i, "weight", parseFloat(e.target.value) || 0)} />
                </FormField>
                <FormField label="Description">
                  <input value={kpi.description} onChange={(e) => updateKPI(i, "description", e.target.value)} />
                </FormField>
                <FormField label="Evaluation Criteria">
                  <input value={kpi.evaluationCriteria} onChange={(e) => updateKPI(i, "evaluationCriteria", e.target.value)} />
                </FormField>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="protrain-scoring-section">
        <h3>Red Flag Phrases</h3>
        <p className="protrain-scoring-hint">Phrases that should be flagged during scoring.</p>
        <div className="protrain-scoring-redflag-input">
          <input value={redFlagInput} onChange={(e) => setRedFlagInput(e.target.value)} placeholder="Add a red flag phrase..." onKeyDown={(e) => e.key === "Enter" && addRedFlag()} />
          <Button variant="secondary" onClick={addRedFlag}>Add</Button>
        </div>
        <div className="protrain-scoring-redflags">
          {config.redFlagPhrases.map((phrase, i) => (
            <span key={i} className="protrain-scoring-redflag-chip">
              {phrase}
              <button onClick={() => removeRedFlag(i)} aria-label={`Remove ${phrase}`}>✕</button>
            </span>
          ))}
          {config.redFlagPhrases.length === 0 && <span className="protrain-scoring-no-flags">No red flag phrases configured.</span>}
        </div>
      </div>

      <div className="protrain-scoring-section">
        <h3>Sales Methodology</h3>
        <FormField label="Methodology Description">
          <textarea value={config.methodology} onChange={(e) => setConfig({ ...config, methodology: e.target.value })} placeholder="Describe your sales methodology for AI scoring context..." />
        </FormField>
      </div>
    </div>
  );
}
